import { type ReactElement } from "react";
import { Navigate } from "react-router";

import { useFeatureFlag } from "@vega-api/extended/auth/useFeatureFlag";
import { type MenuGroupConfig } from "@vega-core/components/Navigation/GroupedMenu/types";
import { m } from "@vega-core/paraglide/messages";
import { getIsLPCoInvestPortal } from "@vega-core/utils/global";
import { VegaModulesPaths } from "@vega-modules/paths";

import { SettingsPage } from "../components/SettingsPage";
import { settingsConfig, type SettingsEntry } from "../settingsConfig";

type SettingsRoute = {
  path: string;
  element: ReactElement;
};

export const useSettingsConfig = (onMenuItemClick: VoidFunction) => {
  const isLPCoInvestPortal = getIsLPCoInvestPortal();
  const { featureFlag } = useFeatureFlag();

  const visibleEntries = settingsConfig.filter((entry) => {
    if (entry.visibility?.hideForLPCoInvest && isLPCoInvestPortal) return false;

    if (
      entry.visibility?.featureFlag &&
      !featureFlag[entry.visibility.featureFlag as keyof typeof featureFlag]
    )
      return false;

    return true;
  });

  const menuConfig = buildMenuConfig(visibleEntries, onMenuItemClick);
  const routes = buildRoutes(visibleEntries);

  return { menuConfig, routes };
};

function buildMenuConfig(
  entries: SettingsEntry[],
  onMenuItemClick: VoidFunction,
): MenuGroupConfig[] {
  const accountItems = entries
    .filter((entry) => entry.group === "account")
    .map((entry) => ({
      label: entry.label(),
      icon: entry.icon,
      route: `${VegaModulesPaths.Settings}/${entry.section}`,
      onClick: onMenuItemClick,
    }));

  const orgItems = entries
    .filter((entry) => entry.group === "organization")
    .map((entry) => ({
      label: entry.label(),
      icon: entry.icon,
      route: `${VegaModulesPaths.Settings}/${entry.section}`,
      onClick: onMenuItemClick,
    }));

  const tabs: MenuGroupConfig[] = [];

  if (accountItems.length > 0) {
    tabs.push({ items: accountItems });
  }

  if (orgItems.length > 0) {
    tabs.push({
      section: m.navTitles_organization(),
      items: orgItems,
    });
  }

  return tabs;
}

function buildRoutes(entries: SettingsEntry[]): SettingsRoute[] {
  const routes: SettingsRoute[] = [];

  for (const entry of entries) {
    if (entry.children) {
      for (const child of entry.children) {
        routes.push({
          path: `${entry.section}/${child.path}`,
          element: <child.component />,
        });
      }
    }

    routes.push({
      path: entry.section,
      element: (
        <SettingsPage title={entry.label()}>
          <entry.component />
        </SettingsPage>
      ),
    });
  }

  routes.push({
    path: "*",
    element: <Navigate replace to={VegaModulesPaths.SettingsPersonalDetails} />,
  });

  return routes;
}
